import type { ReactNode } from 'react';

export interface EmptyStateProps {
  /** Lucide-style outline path(s) for the icon. */
  iconPath?: string;
  heading: string;
  body?: ReactNode;
  /** mist = on white pages (default); white = on mist/tinted pages. */
  tone?: 'mist' | 'white';
  children?: ReactNode;
}

const SEARCH_PATH = 'M21 21l-4.35-4.35M11 19a8 8 0 1 1 0-16 8 8 0 0 1 0 16Z';

/**
 * Friendly empty state — icon medallion, heading, supportive copy, and an
 * optional action. Used wherever a filter or search returns nothing.
 */
export function EmptyState({
  iconPath = SEARCH_PATH,
  heading,
  body,
  tone = 'mist',
  children,
}: EmptyStateProps) {
  return (
    <div
      className={`mt-10 w-full rounded-2xl px-8 py-14 text-center ${
        tone === 'white' ? 'border border-brand-navy/10 bg-white shadow-sm' : 'bg-brand-mist'
      }`}
    >
      <span className="mx-auto flex size-14 items-center justify-center rounded-full bg-brand-royal/10 text-brand-royal">
        <svg
          aria-hidden
          viewBox="0 0 24 24"
          className="size-7"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
        >
          <path d={iconPath} />
        </svg>
      </span>
      <p className="mt-5 text-lg font-extrabold text-brand-navy">{heading}</p>
      {body ? (
        <p className="mx-auto mt-2 max-w-sm text-[15px] leading-relaxed text-brand-navy/70">{body}</p>
      ) : null}
      {children ? <div className="mt-6 flex flex-wrap justify-center gap-3">{children}</div> : null}
    </div>
  );
}
